/**
 * GlobalStore — Vanilla JS singleton store for session-level ERP values.
 *
 * Design:
 *  • sessionStorage-backed — cleared when browser tab closes (session-safe)
 *  • Dispatches 'rcq:store:change' CustomEvent on every mutation so React
 *    hooks (useGlobal) can re-render reactively without polling
 *  • Zero dependencies — safe to import from API service files, utilities,
 *    and anywhere that can't use React hooks
 *
 * Usage (non-React):
 *   import GlobalStore from '@/store/GlobalStore';
 *   import { GK } from '@/store/GlobalKeys';
 *
 *   const gstin = GlobalStore.get(GK.COMPANY_GSTIN);
 *   GlobalStore.set(GK.BUSINESS_DATE, '2026-02-01');
 *   const snap = GlobalStore.snapshot(); // all current values
 */

const PREFIX = 'rcq_gs_';
export const STORE_CHANGE_EVENT = 'rcq:store:change';
export const STORE_READY_EVENT  = 'rcq:store:ready';

// Silent log function for storage operations (only logs in dev)
const logStorageError = (operation, error) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[GlobalStore] ${operation} failed:`, error?.message || 'Unknown error');
  }
};

class _GlobalStore {
  // ── Write ───────────────────────────────────────────────────────────────
  set(key, value) {
    if (!key) return this;
    try {
      sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch (e) {
      logStorageError('sessionStorage.set', e);
      try { localStorage.setItem(PREFIX + key, JSON.stringify(value)); } catch (e2) { logStorageError('localStorage.set', e2); }
    }
    try {
      window.dispatchEvent(
        new CustomEvent(STORE_CHANGE_EVENT, { detail: { key, value } })
      );
    } catch (e) { logStorageError('dispatchEvent', e); }
    return this; // chainable
  }

  /** Batch-set multiple keys at once — dispatches one event per key */
  setMany(obj = {}) {
    Object.entries(obj).forEach(([k, v]) => this.set(k, v));
    return this;
  }

  // ── Read ────────────────────────────────────────────────────────────────
  get(key, fallback = null) {
    if (!key) return fallback;
    try {
      const raw = sessionStorage.getItem(PREFIX + key)
               ?? localStorage.getItem(PREFIX + key);
      return raw !== null ? JSON.parse(raw) : fallback;
    } catch { return fallback; }
  }

  /** Read multiple keys → returns plain object */
  getMany(keys = []) {
    return Object.fromEntries(keys.map(k => [k, this.get(k)]));
  }

  has(key) {
    return sessionStorage.getItem(PREFIX + key) !== null ||
           localStorage.getItem(PREFIX + key) !== null;
  }

  // ── Delete ──────────────────────────────────────────────────────────────
  remove(key) {
    try { sessionStorage.removeItem(PREFIX + key); } catch (e) { logStorageError('sessionStorage.remove', e); }
    try { localStorage.removeItem(PREFIX + key); } catch (e) { logStorageError('localStorage.remove', e); }
    try {
      window.dispatchEvent(
        new CustomEvent(STORE_CHANGE_EVENT, { detail: { key, value: null } })
      );
    } catch (e) { logStorageError('dispatchEvent', e); }
  }

  /** Clear ALL store keys (call on logout) */
  clear() {
    const prefix = PREFIX;
    try {
      Object.keys(sessionStorage).filter(k => k.startsWith(prefix)).forEach(k => sessionStorage.removeItem(k));
      Object.keys(localStorage).filter(k => k.startsWith(prefix)).forEach(k => localStorage.removeItem(k));
    } catch (e) { logStorageError('clear', e); }
    try {
      window.dispatchEvent(
        new CustomEvent(STORE_CHANGE_EVENT, { detail: { key: '*', value: null } })
      );
    } catch (e) { logStorageError('dispatchEvent', e); }
  }

  // ── Inspect ─────────────────────────────────────────────────────────────
  /** Returns all stored values as a plain object */
  snapshot() {
    const result = {};
    try {
      Object.keys(sessionStorage).filter(k => k.startsWith(PREFIX)).forEach(k => {
        const shortKey = k.slice(PREFIX.length);
        result[shortKey] = this.get(shortKey);
      });
    } catch (e) { logStorageError('snapshot', e); }
    return result;
  }

  /** Subscribe to any change. Returns unsubscribe function. */
  subscribe(callback) {
    const handler = (e) => callback(e.detail);
    window.addEventListener(STORE_CHANGE_EVENT, handler);
    return () => window.removeEventListener(STORE_CHANGE_EVENT, handler);
  }
}

const GlobalStore = new _GlobalStore();
export default GlobalStore;
